import React, { useState } from "react";
import { Modal } from "antd";
import FashionTillShell from "./FashionTillShell";
import { formatTillMoney, tillAmountValue } from "./tillFormat";

// Board close-keep (step 3 of close till) plus the close-store confirm dialog.
// Every state change goes through the screen's own onChange /
// handleCashToKeepChange, so the retained amount is computed exactly as before.
const FashionCashToKeep = ({
  outlet,
  till,
  day,
  currency,
  openingAmount,
  countedCash,
  retainedAmount,
  checkedValue,
  cashToKeep,
  allowDifferent,
  optionsDisabled,
  inputRef,
  onKeepChange,
  onAmountChange,
  showCloseShift,
  showCloseStore,
  closingAction,
  onCloseShift,
  onCloseStore,
  onBack,
}) => {
  const opening = tillAmountValue(openingAmount);
  const [differentChosen, setDifferentChosen] = useState(() => allowDifferent && checkedValue === "0" && tillAmountValue(cashToKeep) !== opening);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const mode = checkedValue !== "0" ? "nothing" : differentChosen ? "different" : "float";
  const busy = closingAction !== null && closingAction !== undefined;

  const counted = tillAmountValue(countedCash);
  const keep = tillAmountValue(retainedAmount);
  const safeDrop = Math.max(Math.round((counted - keep) * 100) / 100, 0);

  const choose = (next) => {
    if (optionsDisabled) return;
    if (next === "nothing") {
      setDifferentChosen(false);
      onKeepChange({ target: { checked: false } });
      return;
    }
    onKeepChange({ target: { checked: true } });
    setDifferentChosen(next === "different");
    if (next === "different") setTimeout(() => inputRef?.current?.focus?.(), 0);
  };

  const options = [
    { key: "float", title: `Keep ${formatTillMoney(opening, currency)} as tomorrow's float`, hint: "Same as today's opening cash" },
    allowDifferent ? { key: "different", title: "Keep a different amount" } : null,
    { key: "nothing", title: "Keep nothing · hand over all cash" },
  ].filter(Boolean);

  const meta = [till, outlet, day].filter(Boolean).join(" · ");
  return (
    <FashionTillShell title="Close till" meta={meta} onBack={busy ? undefined : onBack} step={3} testId="fashion-cash-to-keep">
      <section className="ftill-main">
        <div>
          <h1 className="ftill-h1">Cash to keep in the drawer</h1>
          <p className="ftill-sub">The rest goes in the safe drop.</p>
        </div>
        <div className="ftill-options" role="radiogroup" aria-label="Cash to keep">
          {options.map((opt) => (
            <label
              key={opt.key}
              className={`ftill-option${mode === opt.key ? " ftill-option--on" : ""}${optionsDisabled ? " ftill-option--disabled" : ""}`}
            >
              <input type="radio" name="ftill-keep" value={opt.key} checked={mode === opt.key} disabled={optionsDisabled} onChange={() => choose(opt.key)} />
              <span className="ftill-option__text">
                <b>{opt.title}</b>
                {opt.hint ? <small>{opt.hint}</small> : null}
              </span>
            </label>
          ))}
        </div>
        {mode === "different" ? (
          <div className="ftill-field" style={{ maxWidth: 320 }}>
            <label className="ftill-label" htmlFor="ftill-keep-amount">
              Amount to keep
            </label>
            <input
              id="ftill-keep-amount"
              ref={inputRef}
              className="ftill-input ftill-input--amount"
              inputMode="decimal"
              value={cashToKeep}
              onChange={onAmountChange}
            />
          </div>
        ) : null}
        <div className="ftill-summary">
          <div className="ftill-stat">
            <span>Counted cash</span>
            <b>{formatTillMoney(counted, currency)}</b>
          </div>
          <div className="ftill-stat">
            <span>Keep in drawer</span>
            <b>{formatTillMoney(keep, currency)}</b>
          </div>
          <div className="ftill-stat">
            <span>Safe drop</span>
            <b>{formatTillMoney(safeDrop, currency)}</b>
          </div>
        </div>
      </section>
      <aside className="ftill-side">
        <h2 className="ftill-side__title">How do you want to finish?</h2>
        {showCloseShift ? (
          <div className="ftill-finish">
            <b>Close my shift</b>
            <p>The till stays open for the next cashier. Prints your shift report.</p>
            <button type="button" className="ftill-btn ftill-btn--secondary ftill-btn--block" disabled={busy} onClick={onCloseShift}>
              {closingAction === "shift" ? "Closing..." : "Close shift"}
            </button>
          </div>
        ) : null}
        {showCloseStore ? (
          <div className="ftill-finish">
            <b>Close store for the day</b>
            <p>Ends the business day for {outlet || "this store"}. Other tills must be closed first.</p>
            <button type="button" className="ftill-btn ftill-btn--primary ftill-btn--block" disabled={busy} onClick={() => setConfirmOpen(true)}>
              {closingAction === "store" ? "Closing..." : "Close store"}
            </button>
          </div>
        ) : null}
        <div className="ftill-grow" />
        <button type="button" className="ftill-btn ftill-btn--secondary ftill-btn--block" disabled={busy} onClick={onBack}>
          Back
        </button>
      </aside>
      <Modal open={confirmOpen} footer={null} closable={false} centered width={440} onCancel={() => setConfirmOpen(false)} destroyOnClose>
        <div className="ftill" style={{ height: "auto", minHeight: 0, background: "transparent" }} data-testid="fashion-close-store">
          <h2 className="ftill-h1">
            Close {outlet || "the store"} for {day}?
          </h2>
          <p className="ftill-sub">No more bills can be made today after this. The day-end report is sent to the head office.</p>
          <ul className="ftill-check">
            <li>
              <span className="ftill-check__dot" />
              {till ? `${till} counted and checked` : "Till counted and checked"}
            </li>
          </ul>
          <div className="ftill-modal-actions">
            <button type="button" className="ftill-btn ftill-btn--secondary" onClick={() => setConfirmOpen(false)}>
              Go back
            </button>
            <button
              type="button"
              className="ftill-btn ftill-btn--primary"
              onClick={() => {
                setConfirmOpen(false);
                onCloseStore();
              }}
            >
              Close store
            </button>
          </div>
        </div>
      </Modal>
    </FashionTillShell>
  );
};

export default FashionCashToKeep;
